import React, { Fragment, useMemo } from "react";
import { Column } from "@/shared/ui/Blocks";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/shared/ui/Resizable";
import { cn } from "@/shared/utils/shadcn";

interface LayoutWithResizableColumnsProps {
  children?: React.ReactNode;
  columnsGrow?: number[];
  columnsMinSize?: number[];
}

const LayoutWithResizableColumns = React.memo(
  (props: LayoutWithResizableColumnsProps) => {
    const { children, columnsGrow, columnsMinSize } = props;

    const Columns = useMemo(() => {
      const childrenArray = React.Children.toArray(children);
      const childrenCount = childrenArray.length;

      return (
        <>
          {childrenArray.map((child, index) => (
            <Fragment key={`item-${index}`}>
              <ResizablePanel
                className={cn(
                  index === 0 && "overflow-y-auto! overflow-x-hidden"
                )}
                minSize={columnsMinSize ? columnsMinSize[index] : 5}
                defaultSize={
                  columnsGrow ? columnsGrow[index] : 100 / childrenCount
                }
                collapsible={index !== 0}
                collapsedSize={5}
                order={index}
                id={`column-${index}`}
                key={`panel-${index}`}
              >
                {child}
              </ResizablePanel>
              {index < childrenArray.length - 1 && (
                <ResizableHandle key={`handle-${index}`} withHandle />
              )}
            </Fragment>
          ))}
        </>
      );
    }, [children, columnsGrow, columnsMinSize]);

    return (
      <ResizablePanelGroup
        direction="horizontal"
        className="relative flex-1 !h-[calc(100vh-64px)]"
      >
        {Columns}
      </ResizablePanelGroup>
    );
  },
  (prevProps, nextProps) =>
    // Custom comparison function for React.memo
    prevProps.children === nextProps.children &&
    prevProps.columnsGrow === nextProps.columnsGrow &&
    prevProps.columnsMinSize === nextProps.columnsMinSize
);

LayoutWithResizableColumns.displayName = "LayoutWithResizableColumns";

export { LayoutWithResizableColumns };
